// The Terminal log's throttled live announcement (#4401, epic #4391), pulled
// out of `TerminalLog.tsx` (size budget, and one reason to change).
//
// `role="log"` is implicitly `aria-live="polite"`, and at 100 ev/s a screen
// reader announcing every row is unusable. The log sets `aria-live="off"` and
// renders this hook's text in a visually hidden status line instead: at most
// ONE "N new lines" per window. It counts only rows NEWER than the last
// announcement (`countAfter`), so an older page loaded by scroll-back is never
// announced as new, and the rows the page opened on are never announced at all.

import { type Accessor, createEffect, createSignal, onCleanup } from 'solid-js';
import { tPlural } from '../../i18n';
import { type BufferRow, countAfter } from './buffer';

/** The default window: one announcement per five seconds, however hard it bursts. */
export const ANNOUNCE_EVERY_MS = 5_000;

export function useLiveAnnouncement(
  rows: Accessor<readonly BufferRow[]>,
  everyMs: Accessor<number>,
): Accessor<string> {
  const [announcement, setAnnouncement] = createSignal('');
  let announcedThrough: BufferRow | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let speakTimer: ReturnType<typeof setTimeout> | undefined;

  createEffect(() => {
    const current = rows();
    const last = current[current.length - 1];
    if (last === undefined) return;
    if (announcedThrough === null) {
      announcedThrough = { id: last.id, ts: last.ts };
      return;
    }
    if (timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      const now = rows();
      const tail = now[now.length - 1];
      const fresh = announcedThrough === null ? 0 : countAfter(now, announcedThrough);
      if (tail !== undefined) announcedThrough = { id: tail.id, ts: tail.ts };
      if (fresh === 0) return;
      // Clear, then speak on the next tick: the SAME count twice in a row
      // ("1 new line" on a slow stream) must still change the DOM, or a
      // screen reader stays silent on every window after the first.
      setAnnouncement('');
      speakTimer = setTimeout(() => {
        speakTimer = undefined;
        setAnnouncement(tPlural('terminal.live_announce', fresh, { count: fresh }));
      }, 50);
    }, everyMs());
  });
  onCleanup(() => {
    clearTimeout(timer);
    clearTimeout(speakTimer);
  });

  return announcement;
}
